import PropTypes from 'prop-types';
import React, { useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';

import { useTrackEvent } from '../hooks/useTracking';

const ModalsContext = React.createContext(null);

export const useModalsContext = () => useContext(ModalsContext);

export const useModal = () => {
    const {
        modal,
        setModal,
        origin,
        setModalOrigin,
        fullscreenContent,
        setFullscreenContent,
        watched,
        setWatched,
        setExternalLink,
    } = useModalsContext();

    const { type = null } = fullscreenContent || {};

    return {
        modal,
        setModal,
        origin,
        setModalOrigin,
        fullscreenContent,
        setFullscreenContent,
        isFullscreen: fullscreenContent !== null,
        isFullscreenArticle: fullscreenContent !== null && type === 'article',
        watched,
        setWatched,
        setExternalLink,
    };
};

const propTypes = {
    modal: PropTypes.shape({
        type: PropTypes.string,
    }),
    origin: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
    children: PropTypes.node.isRequired,
};

const defaultProps = {
    modal: null,
    origin: null,
};

export const ModalsProvider = ({ modal: initialModal, origin: initialOrigin, children }) => {
    const timerRef = useRef();
    const trackingTimingRef = useRef(null);
    const [modal, setModal] = useState(initialModal);
    const [watched, setWatched] = useState(false);
    const [externalLinkContent, setExternalLink] = useState(null);
    const [fullscreenContent, setFullscreenContentState] = useState(null);
    const [origin, setModalOrigin] = useState(initialOrigin);
    const trackEvent = useTrackEvent();

    const trackModal = useCallback(() => {
        // (content = null, open) => {
        // TODO: Setup modal event tracking for this site
        // const { slug = null } = trackingTimingRef.current || content || {};
        // if (open) {
        //     trackingTimingRef.current = { start: Date.now(), ...content };
        // } else if (trackingTimingRef !== null && trackingTimingRef.current !== null) {
        //     const duration = (Date.now() - trackingTimingRef.current.start) / 1000;
        //     trackEvent(
        //         'Close',
        //         'click',
        //         `Visionnement_${slug}_pendant_${Math.round(duration)}secs`,
        //     );
        // }
    }, [fullscreenContent, trackEvent, trackingTimingRef]);

    const ratio = 20000;

    const setFullscreenContent = useCallback(
        (content = null) => {
            const { points = 1 } = content || {};
            if (content !== null) {
                timerRef.current = setTimeout(() => {
                    setWatched(true);
                }, points * ratio); // change timeout length if too long/too short
                setFullscreenContentState(content);
                trackModal(content, true);
            } else {
                clearTimeout(timerRef.current);
                timerRef.current = null;
                trackModal();
                setFullscreenContentState(null);
            }
        },
        [setFullscreenContentState, trackModal, trackingTimingRef, ratio],
    );

    useEffect(() => {
        if (externalLinkContent !== null) {
            const { points = 1 } = externalLinkContent || {};
            timerRef.current = setTimeout(() => {
                setWatched(true);
            }, points * ratio);
        }

        const onFocus = () => {
            if (externalLinkContent !== null) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
                setExternalLink(null);
            }
        };
        window.addEventListener('focus', onFocus);

        return () => {
            window.removeEventListener('focus', onFocus);
        };
    }, [externalLinkContent]);

    const value = useMemo(
        () => ({
            fullscreenContent,
            setFullscreenContent,
            setExternalLink,
            modal,
            setModal,
            origin,
            setModalOrigin,
            watched,
            setWatched,
        }),
        [
            fullscreenContent,
            setFullscreenContent,
            setExternalLink,
            modal,
            setModal,
            origin,
            setModalOrigin,
            watched,
            setWatched,
        ],
    );

    return <ModalsContext.Provider value={value}>{children}</ModalsContext.Provider>;
};

ModalsProvider.propTypes = propTypes;
ModalsProvider.defaultProps = defaultProps;

export default ModalsContext;
